import React, { useEffect, useRef, useState } from "react";
import chevron from "../assets/select-chevron-sm.svg";

const koulen = { fontFamily: "Koulen, sans-serif" };

// Two looks, one behaviour. "box" is the Figma dropdown spec (node 1155:1159):
// gray rounded trigger with a dark chevron that flips while open, and a
// floating gray panel listing the options. "underline" borrows the Service
// History filter language -- Koulen caps over a rule, active in PrimaryColor/500
// (#1c2938) and the rest in PrimaryColor/300 (#777f88) -- so the Event page's
// filters read as the same family of control as FilterTabs and SearchBar do
// there. Both are palette tokens; the Event page's own #000 is not one.
//
// An option's text colour lives ONLY in optionActive/optionInactive, never in
// option itself: two arbitrary text-[...] utilities on one element are decided
// by stylesheet order, not class order, so a base colour would silently beat
// the active one.
//
// Only "underline" uppercases. It does so in the DOM rather than in CSS, the
// way FilterTabs does, so an accessible name is the text actually on screen.
const STYLES = {
  box: {
    trigger:
      "w-full h-[56px] lg:h-[67px] bg-[#d2d4d7] rounded-[10px] px-[22px] flex items-center justify-between text-[18px] lg:text-[20px] focus:outline-none focus:ring-2 focus:ring-[#1c2938]/30",
    selected: "text-[#1c2938]",
    placeholder: "text-[#515151]",
    panel:
      "absolute z-10 top-full mt-[19px] w-full bg-[#d2d4d7] rounded-[10px] py-[10px] max-h-[320px] overflow-y-auto",
    option:
      "w-full text-left px-[22px] py-[10px] text-[18px] lg:text-[20px] hover:bg-[#c3c6ca]",
    optionActive: "text-[#515151]",
    optionInactive: "text-[#515151]",
  },
  underline: {
    font: koulen,
    uppercase: true,
    trigger:
      "w-full bg-transparent border-b-[3px] border-[#1c2938] pb-[4px] flex items-center justify-between gap-[8px] text-[20px] sm:text-[24px] tracking-[1.5px] focus:outline-none",
    selected: "text-[#1c2938]",
    placeholder: "text-[#777f88]",
    panel:
      "absolute z-10 top-full mt-[10px] w-full bg-white border-[2px] border-[#1c2938] py-[6px] max-h-[320px] overflow-y-auto",
    option:
      "w-full text-left px-[14px] py-[5px] text-[20px] tracking-[1.5px] hover:bg-[#f0f1f2]",
    optionActive: "text-[#1c2938]",
    optionInactive: "text-[#777f88]",
  },
};

export default function Dropdown({ value, onChange, options, placeholder, variant = "box" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const styles = STYLES[variant] ?? STYLES.box;
  const label = (text) =>
    styles.uppercase && typeof text === "string" ? text.toUpperCase() : text;

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={styles.trigger}
        style={styles.font}
      >
        <span className={selected ? styles.selected : styles.placeholder}>
          {selected ? label(selected.label) : label(placeholder)}
        </span>
        <img
          src={chevron}
          alt=""
          className={`w-[15px] h-[11px] shrink-0 ${open ? "-scale-y-100" : ""}`}
        />
      </button>
      {open && (
        <ul role="listbox" className={styles.panel}>
          {options.map((o) => (
            <li key={o.value} role="option" aria-selected={o.value === value}>
              <button
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`${styles.option} ${
                  o.value === value ? styles.optionActive : styles.optionInactive
                }`}
                style={styles.font}
              >
                {label(o.label)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
